import { useTheme } from '@emotion/react';
import { Delete } from '@icon-park/react';
import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import type { LearningResource } from '@didaskill/domain/learning-resources/types';
import {
  AlertDialog,
  HtmlFormatter,
  ICON_SIZES,
  removeButtonAppearance,
  Typography,
  UnexpectedErrorToast,
} from '@didaskill/ui/design-system';
import { useBoolState } from '@didaskill/ui/utils';

import messages from './LearningResourceDeleteButton.messages';

type LearningResourceDeleteButtonProps = {
  learningResource: LearningResource;
  onDeleteLearningResource: (learningResourceId: string) => Promise<void>;
  fromReference?: boolean;
};

const LearningResourceDeleteButtonBase = ({
  learningResource,
  onDeleteLearningResource,
  fromReference = false,
}: LearningResourceDeleteButtonProps) => {
  const theme = useTheme();
  const intl = useIntl();

  const [isConfirmDeletionOpen, onOpenConfirmDeletion, onCloseConfirmDeletion] =
    useBoolState(false);

  const handleDeleteLearningResource = async () => {
    try {
      await onDeleteLearningResource(learningResource.id);
      onCloseConfirmDeletion();
    } catch (error) {
      toast.error(<UnexpectedErrorToast errorMessage={error.response?.body?.more ?? null} />);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => {
          onOpenConfirmDeletion();
        }}
        aria-label={intl.formatMessage(messages.DELETE_LEARNING_RESOURCE)}
        css={removeButtonAppearance}
      >
        <Delete
          size={ICON_SIZES.MEDIUM}
          fill={[theme.colors.danger[500], theme.colors.danger[50]]}
        />
      </button>

      <AlertDialog
        isOpen={isConfirmDeletionOpen}
        onRequestClose={onCloseConfirmDeletion}
        onClickConfirm={handleDeleteLearningResource}
        variant={AlertDialog.VARIANTS.DANGER}
      >
        <HtmlFormatter>
          <Typography variant={Typography.VARIANTS.HEADING_4}>
            <FormattedMessage {...messages.CONFIRM_RESOURCE_DELETION_TITLE} />
          </Typography>

          <Typography variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR}>
            <FormattedMessage
              {...messages.CONFIRM_RESOURCE_DELETION_BODY}
              values={{
                title: learningResource.title,
                fromReference,
                b: (chunks) => (
                  <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>{chunks}</Typography>
                ),
              }}
            />
          </Typography>
        </HtmlFormatter>
      </AlertDialog>
    </div>
  );
};

LearningResourceDeleteButtonBase.displayName = 'LearningResourceDeleteButton';

export const LearningResourceDeleteButton = memo(LearningResourceDeleteButtonBase);
